import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useComboboxTypeahead } from "@/hooks/useComboboxTypeahead";
import type { ExpenseCategoryRecord } from "@/lib/expenses/expenseCategoriesStore";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type ExpenseSubcategorySelectProps = {
  value: string;
  subcategories: ExpenseCategoryRecord[];
  onChange: (id: string) => void;
  placeholder?: string;
  triggerClassName?: string;
  disabled?: boolean;
  includeAllOption?: boolean;
  allValue?: string;
  allLabel?: string;
};

function itemValue(id: string): string {
  return `subcategory:${id}`;
}

export function ExpenseSubcategorySelect({
  value,
  subcategories,
  onChange,
  placeholder = "Select subcategory",
  triggerClassName,
  disabled = false,
  includeAllOption = false,
  allValue = "all",
  allLabel = "All",
}: ExpenseSubcategorySelectProps) {
  const { open, setOpen, onTriggerKeyDown, onOpenChange, commandInputProps } =
    useComboboxTypeahead(disabled);
  const [highlight, setHighlight] = useState("");

  const selected = useMemo(
    () => subcategories.find((c) => c.id === value) ?? null,
    [subcategories, value],
  );

  const groups = useMemo(() => {
    const byParent = new Map<string, ExpenseCategoryRecord[]>();
    for (const item of subcategories) {
      const parent = (item.accountDetailTypeTitle ?? "").trim() || "Other";
      const list = byParent.get(parent) ?? [];
      list.push(item);
      byParent.set(parent, list);
    }
    return Array.from(byParent.entries()).map(([parent, items]) => ({
      parent,
      items: [...items].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
    }));
  }, [subcategories]);

  const isAllSelected = includeAllOption && value === allValue;
  const displayLabel = isAllSelected ? allLabel : selected ? selected.name : placeholder;

  const syncHighlightToValue = () => {
    if (includeAllOption && value === allValue) {
      setHighlight(itemValue(allValue));
      return;
    }
    setHighlight(value ? itemValue(value) : "");
  };

  useEffect(() => {
    if (!open) return;
    if (includeAllOption && value === allValue) {
      setHighlight(itemValue(allValue));
    } else {
      setHighlight(value ? itemValue(value) : "");
    }
    const frame = requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>("[data-expense-subcategory-selected='true']")
        ?.scrollIntoView({ block: "nearest" });
    });
    return () => cancelAnimationFrame(frame);
  }, [open, value, includeAllOption, allValue]);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (next) syncHighlightToValue();
      }}
      modal
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          onKeyDown={onTriggerKeyDown}
          className={cn(
            "glass-input h-10 w-full justify-between px-3 font-normal text-sm",
            !selected && !isAllSelected && "text-muted-foreground",
            triggerClassName,
          )}
        >
          <span className="truncate">{displayLabel}</span>
          <ChevronsUpDown className="ml-1.5 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 z-[100]" align="start" sideOffset={4}>
        <Command
          value={highlight}
          onValueChange={setHighlight}
          filter={(item, query, keywords) => {
            const q = query.trim().toLowerCase();
            if (!q) return 1;
            const haystack = `${(keywords ?? []).join(" ")} ${item}`.toLowerCase();
            return haystack.includes(q) ? 1 : 0;
          }}
        >
          <CommandInput
            {...commandInputProps}
            placeholder="Search subcategory..."
            className="h-9 text-xs"
          />
          <CommandList className="max-h-[240px] overflow-y-auto">
            <CommandEmpty className="py-2.5 text-center text-xs text-muted-foreground">
              No subcategory found.
            </CommandEmpty>
            {includeAllOption ? (
              <CommandGroup>
                <CommandItem
                  value={itemValue(allValue)}
                  keywords={[allLabel]}
                  data-expense-subcategory-selected={isAllSelected ? "true" : undefined}
                  onSelect={() => {
                    onChange(allValue);
                    setOpen(false);
                  }}
                  className="text-xs flex items-center justify-between cursor-pointer"
                >
                  <span className="truncate font-medium">{allLabel}</span>
                  <Check
                    className={cn(
                      "ml-2 h-3.5 w-3.5 shrink-0 text-primary",
                      isAllSelected ? "opacity-100" : "opacity-0",
                    )}
                  />
                </CommandItem>
              </CommandGroup>
            ) : null}
            {groups.map((group) => (
              <CommandGroup key={group.parent} heading={group.parent}>
                {group.items.map((c) => {
                  const isSelected = c.id === value;
                  return (
                    <CommandItem
                      key={c.id}
                      value={itemValue(c.id)}
                      keywords={[c.name ?? "", c.accountDetailTypeTitle ?? "", c.number ?? ""]}
                      data-expense-subcategory-selected={isSelected ? "true" : undefined}
                      onSelect={() => {
                        onChange(c.id);
                        setOpen(false);
                      }}
                      className="text-xs flex items-center justify-between cursor-pointer"
                    >
                      <span className="break-words font-medium">{c.name}</span>
                      <Check
                        className={cn(
                          "ml-2 h-3.5 w-3.5 shrink-0 text-primary",
                          isSelected ? "opacity-100" : "opacity-0",
                        )}
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
